import { useState, useEffect, useCallback, useRef, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import {
  BarChart3,
  ListChecks,
  Clock,
  CheckCircle,
  AlertTriangle,
  TrendingUp,
  Calendar,
  User,
  Target,
  Timer,
  Plus,
  UserCheck
} from 'lucide-react'
import { Task, TaskStats, TaskPriority, TaskCategory } from '../types/tasks'
import { taskService } from '../services/task-service'
import { useAuth } from '../contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/utils/cn'
import { formatTaskDueDate, isTaskDueToday, isTaskOverdue } from '../utils/task-date'

import { getErrorMessage } from '../utils/error.utils'
type BadgeVariant = 'default' | 'secondary' | 'outline' | 'destructive'

interface DashboardData {
  stats: TaskStats
  myTasks: Task[]
  urgentTasks: Task[]
  dueTodayTasks: Task[]
  recentTasks: Task[]
}

export function TaskDashboard() {
  const { user } = useAuth()
  const [data, setData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const requestSeqRef = useRef(0)

  const loadDashboardData = useCallback(async () => {
    const requestSeq = requestSeqRef.current + 1
    requestSeqRef.current = requestSeq

    try {
      setLoading(true)
      setError(null)

      const [statsResponse, myTasksResponse] = await Promise.all([
        taskService.getStats(user?.id ? { assigned_user_id: user.id } : undefined),
        taskService.findAll(
          user?.id ? { assigned_user_id: user.id } : undefined,
          { limit: 50 }
        )
      ])

      const stats = statsResponse.data.stats
      const myTasks = myTasksResponse.data.tasks

      if (requestSeqRef.current !== requestSeq) return

      setData({
        stats,
        myTasks,
        urgentTasks: myTasks.filter(t => t.priority === 'URGENT'),
        dueTodayTasks: myTasks.filter(isTaskDueToday),
        recentTasks: myTasks.slice(0, 5)
      })

    } catch (err: unknown) {
      if (requestSeqRef.current === requestSeq) {
        setError(getErrorMessage(err, 'Erro ao carregar dashboard'))
      }
    } finally {
      if (requestSeqRef.current === requestSeq) {
        setLoading(false)
      }
    }
  }, [user?.id])

  useEffect(() => {
    loadDashboardData()
  }, [loadDashboardData])

  const formatDate = (dateString: string) => {
    return formatTaskDueDate(dateString)
  }

  const isOverdue = (task: Task) => {
    return isTaskOverdue(task)
  }

  const getPriorityBadge = (priority: TaskPriority) => {
    const priorityMap: Record<TaskPriority, { variant: BadgeVariant; label: string }> = {
      LOW: { variant: 'outline', label: 'Baixa' },
      MEDIUM: { variant: 'secondary', label: 'Média' },
      HIGH: { variant: 'default', label: 'Alta' },
      URGENT: { variant: 'destructive', label: 'Urgente' }
    }

    const config = priorityMap[priority]
    return <Badge variant={config.variant}>{config.label}</Badge>
  }

  const getStatusBadge = (status: Task['status']) => {
    const statusMap: Record<Task['status'], { variant: BadgeVariant; label: string }> = {
      PENDING: { variant: 'outline', label: 'Pendente' },
      IN_PROGRESS: { variant: 'default', label: 'Em Andamento' },
      WAITING: { variant: 'outline', label: 'Aguardando' },
      FOR_REVIEW: { variant: 'secondary', label: 'Em Revisão' },
      COMPLETED: { variant: 'secondary', label: 'Concluída' },
      CANCELLED: { variant: 'destructive', label: 'Cancelada' }
    }

    const config = statusMap[status]
    return <Badge variant={config.variant}>{config.label}</Badge>
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-4 p-4 sm:p-6" aria-busy="true">
        <span className="sr-only">Carregando dashboard...</span>
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-28" />)}
        </div>
        <Skeleton className="h-64" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="flex flex-col items-center gap-2 p-4 py-12 text-center sm:p-6">
        <AlertTriangle className="size-10 text-destructive" aria-hidden="true" />
        <h3 className="text-lg font-medium">Erro ao carregar dashboard</h3>
        <p className="text-sm text-muted-foreground">{error}</p>
        <Button className="mt-2" onClick={loadDashboardData}>
          Tentar novamente
        </Button>
      </div>
    )
  }

  const { stats } = data
  const total = stats.total
  const completionRate = total > 0
    ? Math.round((stats.by_status.completed / total) * 100)
    : 0

  const priorityEntries: [string, number][] = [
    ['LOW', stats.by_priority.low],
    ['MEDIUM', stats.by_priority.medium],
    ['HIGH', stats.by_priority.high],
    ['URGENT', stats.by_priority.urgent],
  ]

  const categoryEntries: [TaskCategory, number][] = [
    ['PRAZO_PROCESSUAL', data.myTasks.filter(t => t.category === 'PRAZO_PROCESSUAL').length],
    ['ACAO_INTERNA', data.myTasks.filter(t => t.category === 'ACAO_INTERNA').length],
    ['FOLLOW_UP', data.myTasks.filter(t => t.category === 'FOLLOW_UP').length],
    ['DOCUMENTO', data.myTasks.filter(t => t.category === 'DOCUMENTO').length],
    ['OUTRO', data.myTasks.filter(t => t.category === 'OUTRO').length],
  ]

  const categoryLabels: Record<TaskCategory, string> = {
    PRAZO_PROCESSUAL: 'Prazo Processual',
    ACAO_INTERNA: 'Ação Interna',
    FOLLOW_UP: 'Acompanhamento',
    DOCUMENTO: 'Documento',
    OUTRO: 'Outro',
  }

  const kpis = [
    {
      label: 'Total de Tarefas', value: stats.total, icon: Target,
      footer: (
        <div className="flex justify-between gap-2 text-xs text-muted-foreground">
          <span>Pendentes: {stats.by_status.pending}</span>
          <span>Concluídas: {stats.by_status.completed}</span>
        </div>
      )
    },
    { label: 'Em Andamento', value: stats.by_status.in_progress, icon: Clock, footer: <p className="text-xs text-muted-foreground">Vencendo esta semana: {stats.due_this_week}</p> },
    { label: 'Em Revisão', value: stats.by_status.for_review, icon: UserCheck, footer: <p className="text-xs text-muted-foreground">Aguardando aprovação</p> },
    { label: 'Em Atraso', value: stats.overdue, icon: AlertTriangle, alert: stats.overdue > 0, footer: <p className="text-xs text-muted-foreground">Vencendo hoje: {stats.due_today}</p> },
    {
      label: 'Taxa de Conclusão', value: `${completionRate}%`, icon: CheckCircle,
      footer: (
        <div className="h-2 w-full rounded-full bg-muted">
          <div className="h-2 rounded-full bg-primary transition-all duration-300" style={{ width: `${completionRate}%` }} />
        </div>
      )
    }
  ]

  const quickActions: { to: string; label: string; count: number; alert?: boolean }[] = [
    { to: '/tasks?status=overdue', label: 'Tarefas em Atraso', count: stats.overdue, alert: stats.overdue > 0 },
    { to: '/tasks?dueDate=today', label: 'Vencendo Hoje', count: stats.due_today },
    { to: '/tasks?priority=URGENT', label: 'Urgentes', count: stats.by_priority.urgent },
    { to: '/tasks?status=IN_PROGRESS', label: 'Em Andamento', count: stats.by_status.in_progress }
  ]

  const distribution = (title: string, Icon: typeof BarChart3, rows: { key: string; label: ReactNode; count: number }[]) => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Icon className="size-4 text-muted-foreground" aria-hidden="true" />
          <h3>{title}</h3>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {rows.map(({ key, label, count }) => (
          <div key={key} className="flex items-center justify-between gap-3">
            <div className="min-w-0 text-sm text-muted-foreground">{label}</div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium tabular-nums">{count}</span>
              <div className="h-2 w-16 rounded-full bg-muted">
                <div
                  className="h-2 rounded-full bg-primary transition-all duration-300"
                  style={{ width: total > 0 ? `${(count / total) * 100}%` : '0%' }}
                />
              </div>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  )

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex items-center gap-2">
            <ListChecks className="size-6 shrink-0 text-muted-foreground" />
            <h1 className="text-2xl font-semibold">Dashboard de Tarefas</h1>
          </div>
          <p className="text-sm text-muted-foreground">Visão geral das suas tarefas e produtividade</p>
        </div>
        <Button asChild>
          <Link to="/tasks/create"><Plus className="size-4" /> Nova Tarefa</Link>
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {kpis.map(({ label, value, icon: Icon, alert, footer }) => (
          <Card key={label} size="sm">
            <CardContent className="flex h-full flex-col justify-between gap-3">
              <div className="flex items-center gap-3">
                <Icon className={cn('size-5 shrink-0', alert ? 'text-destructive' : 'text-muted-foreground')} aria-hidden="true" />
                <div className="min-w-0">
                  <p className="truncate text-xs text-muted-foreground">{label}</p>
                  <p className={cn('text-lg font-semibold tabular-nums', alert && 'text-destructive')}>{value}</p>
                </div>
              </div>
              {footer}
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {distribution('Por Prioridade', BarChart3, priorityEntries.map(([priority, count]) => ({
          key: priority, label: getPriorityBadge(priority as TaskPriority), count
        })))}

        {distribution('Por Categoria', TrendingUp, categoryEntries.map(([category, count]) => ({
          key: category, label: categoryLabels[category], count
        })))}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Timer className="size-4 text-muted-foreground" aria-hidden="true" />
              <h3>Ações Rápidas</h3>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            {quickActions.map(({ to, label, count, alert }) => (
              <Link
                key={to}
                to={to}
                className={cn(
                  'flex items-center justify-between gap-2 rounded-lg border p-3 transition-colors hover:bg-muted',
                  alert && 'border-destructive/30'
                )}
              >
                <span className="text-sm font-medium">{label}</span>
                <Badge variant={alert ? 'destructive' : 'secondary'}>{count}</Badge>
              </Link>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="size-4 text-muted-foreground" aria-hidden="true" />
            <h3>Tarefas Recentes</h3>
          </CardTitle>
          <Button variant="outline" size="sm" asChild>
            <Link to="/tasks">Ver Todas</Link>
          </Button>
        </CardHeader>
        <CardContent>
          <ul className="flex flex-col divide-y">
            {data.recentTasks.map((task) => (
              <li key={task.id} className="flex flex-col gap-3 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 flex-1">
                  <Link to={`/tasks/${task.id}`} className="wrap-break-word font-medium hover:text-primary hover:underline underline-offset-4">
                    {task.title}
                  </Link>
                  <div className="mt-1 flex flex-wrap items-center gap-2 sm:gap-4">
                    {getPriorityBadge(task.priority)}
                    {getStatusBadge(task.status)}
                    <span className={cn('text-xs', isOverdue(task) ? 'font-medium text-destructive' : 'text-muted-foreground')}>
                      <Calendar className="mr-1 inline size-3" aria-hidden="true" />
                      {formatDate(task.due_date)}
                      {isOverdue(task) && ' (Atrasada)'}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-2 sm:justify-end">
                  <div className="text-xs text-muted-foreground">
                    <User className="mr-1 inline size-3" aria-hidden="true" />
                    {task.assigned_user_name}
                  </div>
                  <Button size="sm" variant="outline" asChild>
                    <Link to={`/tasks/${task.id}/edit`}>Editar</Link>
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  )
}
